import { logger } from '@utils/safeLogger'
import type { ApiResponse } from '../types/api-response'

export class ApiError extends Error {
  readonly statusCode: number
  readonly errorCode?: string
  readonly cause?: unknown

  constructor(
    statusCode: number,
    message: string,
    options?: { errorCode?: string; cause?: unknown },
  ) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.errorCode = options?.errorCode
    this.cause = options?.cause
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Normalize axios / unknown failures into ApiError.
 */
export function toApiError(
  error: unknown,
  fallbackMessage = 'An unknown error occurred',
): ApiError {
  if (error instanceof ApiError) {
    return error
  }

  if (isRecord(error) && 'isAxiosError' in error && error.isAxiosError) {
    const axiosErr = error as {
      message?: string
      response?: {
        status?: number
        data?: { message?: string; error?: string }
      }
    }
    const statusCode = axiosErr.response?.status ?? 500
    const message =
      axiosErr.response?.data?.message || axiosErr.message || fallbackMessage
    const errorCode = axiosErr.response?.data?.error
    return new ApiError(statusCode, message, {
      errorCode,
      cause: error,
    })
  }

  if (error instanceof Error) {
    return new ApiError(500, error.message || fallbackMessage, {
      cause: error,
    })
  }

  return new ApiError(500, fallbackMessage, { cause: error })
}

/**
 * Throw if envelope statusCode is not 2xx; otherwise return data.
 */
export function assertOk<T>(response: ApiResponse<T>): T {
  const code = response.statusCode
  if (code < 200 || code >= 300) {
    throw new ApiError(code || 500, response.message || 'Request failed')
  }
  return response.data
}

export function rethrowApiError(
  error: unknown,
  fallbackMessage?: string,
): never {
  const apiError = toApiError(error, fallbackMessage)
  logger.error(apiError.message, apiError.cause ?? apiError)
  throw apiError
}

export function getErrorMessage(
  error: unknown,
  fallback = 'An unknown error occurred',
): string {
  return toApiError(error, fallback).message
}
